import React, { useEffect, useState } from 'react';
import { db } from 'Compass/profile-lab/services/db';
import { syncService } from 'Compass/profile-lab/services/sync';
import { formatRelativeTime } from 'Compass/profile-lab/utils/timeFormatting';
import { ButtonGroup, ButtonLoad, ButtonReset, ButtonSync } from 'Compass/profile-lab/styles/buttons';
import {
  GrowCount,
  SelectedCount,
  StatItem,
  StatLabel,
  StatValue,
  SubjectContainer,
  SubjectEmpty,
  SubjectError,
  SubjectForm,
  SubjectInput,
  SubjectPage,
  SubjectSection,
  SubjectStats
} from 'Compass/profile-lab/styles/subject';

const ProfileLabSubjectPage: React.FC = () => {
  const [userLid, setUserLid] = useState('');
  const [currentSubject, setCurrentSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    photos: 0,
    chatHistory: 0,
    selectedCount: 0,
    growCount: 0,
    lastSyncTime: 0
  });

  useEffect(() => {
    void loadStats();
    void loadCurrentSubject();
  }, []);

  const loadCurrentSubject = async () => {
    try {
      const config = await db.getSubjectConfig();
      setCurrentSubject(config.userLid);
      setUserLid(config.userLid);
    } catch (err) {
      console.error('Failed to load current subject:', err);
    }
  };

  const loadStats = async () => {
    try {
      const dbStats = await db.getDBStats();
      setStats({
        photos: dbStats.photos,
        chatHistory: dbStats.chatHistory,
        selectedCount: dbStats.selections.selected.length,
        growCount: dbStats.selections.grow.length,
        lastSyncTime: dbStats.syncMetadata.lastSyncTimestamp
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleLoadUser = async () => {
    if (!userLid.trim()) {
      setError('Please enter a user LID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await db.clearPhotos();
      await db.setSubjectConfig(userLid.trim());
      await syncService.loadUserData(userLid.trim());
      setCurrentSubject(userLid.trim());
      await loadStats();
      setError(null);
    } catch (err) {
      setError(`Failed to set subject and load data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!currentSubject) {
      setError('Please set a subject first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await syncService.loadUserData(currentSubject);
      await loadStats();
      setError(null);
    } catch (err) {
      setError(`Failed to sync user data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetDB = async () => {
    if (!confirm('Are you sure you want to reset the database? This will clear all data.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await db.resetDB();
      setCurrentSubject('');
      setUserLid('');
      await loadStats();
    } catch (err) {
      setError(`Failed to reset database: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };


  return (
    <SubjectPage>
      <SubjectContainer>
        <h2>Profile Lab Subject Configuration</h2>

        <SubjectSection>
          <h3>Current Subject</h3>
          {currentSubject ? (
            <SubjectStats>
              <StatItem>
                <StatLabel>User LID:</StatLabel>
                <StatValue>{currentSubject}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Photos:</StatLabel>
                <StatValue>{stats.photos.toLocaleString()}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Chat History:</StatLabel>
                <StatValue>{stats.chatHistory.toLocaleString()}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Selected:</StatLabel>
                <SelectedCount>{stats.selectedCount.toLocaleString()}</SelectedCount>
              </StatItem>
              <StatItem>
                <StatLabel>Grow:</StatLabel>
                <GrowCount>{stats.growCount.toLocaleString()}</GrowCount>
              </StatItem>
              <StatItem>
                <StatLabel>Sync:</StatLabel>
                <StatValue>{formatRelativeTime(stats.lastSyncTime)}</StatValue>
              </StatItem>
            </SubjectStats>
          ) : (
            <SubjectEmpty>No subject loaded</SubjectEmpty>
          )}
        </SubjectSection>

        <SubjectSection>
          <h3>Set Subject</h3>
          <SubjectForm as="div">
            <SubjectInput
              type="text"
              value={userLid}
              onChange={(e) => setUserLid(e.target.value)}
              placeholder="Enter Dazzle user LID"
              disabled={loading}
            />
            <ButtonLoad
              onClick={handleLoadUser}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load Subject'}
            </ButtonLoad>
          </SubjectForm>
          {error && <SubjectError>{error}</SubjectError>}
        </SubjectSection>

        <SubjectSection>
          <h3>Database Management</h3>
          <ButtonGroup>
            <ButtonSync
              onClick={handleSync}
              disabled={loading || !currentSubject}
            >
              {loading ? 'Syncing...' : 'Sync Now'}
            </ButtonSync>
            <ButtonReset
              onClick={handleResetDB}
              disabled={loading}
            >
              Reset Database
            </ButtonReset>
          </ButtonGroup>
        </SubjectSection>
      </SubjectContainer>
    </SubjectPage>
  );
};

export default ProfileLabSubjectPage;
